import React from 'react';
import { 
  Card, Grid, Box, Typography, Button, 
  CardMedia, Tooltip, useTheme, Chip, useMediaQuery
} from '@mui/material';
import { logAnalyticsEvent } from '../../utils/analytics';

/**
 * A simpler version of ProductCard optimized for the supplements table
 * This component focuses on clear visibility of product name and view button
 * with layout stability regardless of API data variations
 */
const StackProductCard = ({ product }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  
  // Extract and format product data with proper fallbacks
  const displayTitle = product?.product_name || product?.title || 'Product';
  
  // Format form factor and nutrient form with proper capitalization
  const formatString = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : null;
  const formattedFormFactor = formatString(product?.form_factor);
  const formattedNutrientForm = formatString(product?.specific_nutrient_form);
  
  // Extract and deduplicate certifications
  const extractCertifications = () => {
    const certifications = [
      ...(product?.usda_organic ? ['USDA Organic'] : []),
      ...(Array.isArray(product?.testing_3rd_party) ? product.testing_3rd_party : []),
      ...(Array.isArray(product?.other_certifications) ? product.other_certifications : []),
      ...(Array.isArray(product?.dietary_attributes) ? product.dietary_attributes : [])
    ].filter(Boolean);
    
    // Deduplicate based on lowercase comparison
    return [...new Set(
      certifications.map(cert => typeof cert === 'string' ? cert.toLowerCase() : '')
    )].map(lowerCert => {
      if (!lowerCert) return null;
      return certifications.find(cert => 
        typeof cert === 'string' && cert.toLowerCase() === lowerCert
      ) || lowerCert;
    }).filter(Boolean);
  };
  
  const uniqueCertifications = extractCertifications();
  
  return (
    <Card sx={{ 
      width: '100%',
      height: '100%',
      minHeight: '8.125rem',
      maxHeight: { xs: '9.0625rem', sm: '9.375rem' },
      borderRadius: '0.5rem',
      boxShadow: '0 0.125rem 0.25rem rgba(0,0,0,0.08)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 0.25rem 0.5rem rgba(0,0,0,0.12)',
      }
    }}>
      <Grid container sx={{ height: '100%' }}>
        {/* Product Image */}
        <Grid item sx={{ width: { xs: 70, sm: 90 }, height: '100%' }}>
          <Box sx={{ 
            height: '100%', 
            backgroundColor: '#f9f9f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1, sm: 1.5 }
          }}>
            <CardMedia
              component="img"
              sx={{ 
                width: '100%',
                height: 'auto',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              image={product?.image_url || product?.imageUrl || 'https://via.placeholder.com/60'}
              alt={displayTitle}
            />
          </Box>
        </Grid>
        
        {/* Content Area */}
        <Grid item xs sx={{ position: 'relative' }}>
          <Box sx={{ 
            height: '100%',
            display: 'flex', 
            flexDirection: 'column',
            padding: { xs: '0.375rem 0.625rem 1.875rem 0.625rem', sm: '0.5rem 0.75rem 1.875rem 0.75rem' },
            position: 'relative',
          }}>
            {/* Product Title Section */}
            <Box sx={{ 
              height: { xs: '1.375rem', sm: '1.5rem' },
              mb: { xs: 0.7, sm: 0.8 },
              width: { xs: 'calc(100% - 2.8125rem)', sm: 'calc(100% - 3.4375rem)' },
              zIndex: 2,
              position: 'relative'
            }}>
              <Tooltip 
                title={displayTitle}
                placement="top"
                arrow
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '0.68rem', sm: '0.75rem' },
                    lineHeight: 1.3,
                    color: 'rgba(0, 0, 0, 0.87)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    position: 'absolute',
                    width: '100%',
                    top: 0,
                    left: 0
                  }}
                >
                  {/* Remove brand emphasis and just show the title */}
                  {displayTitle}
                </Typography>
              </Tooltip>
            </Box>

            {/* Form Factor & Nutrient Form Tags */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: 0.4, sm: 0.5 },
              mb: { xs: 0.5, sm: 0.7 },
              maxHeight: { xs: '1.125rem', sm: '1.25rem' },
              overflow: 'hidden'
            }}>
              {formattedFormFactor && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                    lineHeight: 1.2,
                    padding: { xs: '0rem 0.25rem', sm: '0.0625rem 0.375rem' },
                    borderRadius: '0.1875rem',
                    backgroundColor: 'rgba(245, 245, 245, 0.9)',
                    border: '0.0625rem solid rgba(230, 230, 230, 0.9)',
                    color: 'rgba(80, 80, 80, 0.98)',
                    display: 'inline-block',
                    maxWidth: { xs: '5rem', sm: '6.25rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    height: { xs: '0.9375rem', sm: '1.125rem' },
                  }}
                >
                  {formattedFormFactor}
                </Typography>
              )}
              
              {formattedNutrientForm && (
                <Tooltip title={formattedNutrientForm} placement="top" arrow>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      lineHeight: 1.2,
                      padding: { xs: '0rem 0.25rem', sm: '0.0625rem 0.375rem' },
                      borderRadius: '0.1875rem',
                      backgroundColor: 'rgba(232, 244, 255, 0.9)',
                      border: '0.0625rem solid rgba(210, 230, 250, 0.9)',
                      color: 'rgba(50, 85, 135, 0.98)',
                      display: 'inline-block',
                      maxWidth: { xs: '11.25rem', sm: '16.25rem' },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      height: { xs: '0.9375rem', sm: '1.125rem' },
                    }}
                  >
                    {formattedNutrientForm}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            
            {/* Serving Information */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: { xs: '0 0.375rem', sm: '0 0.5rem' },
              mb: { xs: 0.8, sm: 0.9 },
              maxHeight: { xs: '2.125rem', sm: '2.25rem' },
              overflow: 'hidden'
            }}>
              {product?.serving_size && (
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.63rem', sm: '0.67rem' },
                  color: 'rgba(70, 70, 70, 0.95)',
                  lineHeight: { xs: 1.9, sm: 1.7 },
                }}>
                  <strong style={{ fontWeight: 600 }}>Serving:</strong> {product.serving_size} {product?.form_factor || 'units'}
                </Typography>
              )}
              
              {product?.dosage_amount && (
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.63rem', sm: '0.67rem' },
                  color: 'rgba(70, 70, 70, 0.95)',
                  lineHeight: { xs: 1.9, sm: 1.7 },
                }}>
                  <strong style={{ fontWeight: 600 }}>Contains:</strong> {product.dosage_amount}{product?.dosage_unit || ''}
                </Typography>
              )}
              
              {product?.servings_per_bottle && (
                <Typography variant="caption" sx={{ 
                  fontSize: { xs: '0.63rem', sm: '0.67rem' },
                  color: 'rgba(70, 70, 70, 0.95)',
                  lineHeight: { xs: 1.9, sm: 1.7 },
                }}>
                  <strong style={{ fontWeight: 600 }}>Total:</strong> {product.servings_per_bottle} servings
                </Typography>
              )}
            </Box>
            
            {/* Certifications - Full-length on desktop, truncated on mobile */}
            {uniqueCertifications.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: { xs: 0.3, sm: 0.4 },
                mt: { xs: 0.4, sm: 0.3 },
                maxHeight: { xs: '2.25rem', sm: '2.5rem' },
                overflow: 'hidden'
              }}>
                {/* Show all certs on desktop, limit on mobile */}
                {(isDesktop ? uniqueCertifications : uniqueCertifications.slice(0, 3)).map((cert) => (
                  <Tooltip key={cert} title={cert} placement="top" arrow>
                    <Chip
                      label={cert}
                      size="small"
                      sx={{
                        height: { xs: '0.9375rem', sm: '1.125rem' },
                        fontSize: { xs: '0.53rem', sm: '0.6rem' },
                        backgroundColor: 'rgba(240, 248, 240, 0.95)',
                        color: 'rgba(70, 90, 70, 0.95)',
                        '& .MuiChip-label': { 
                          px: { xs: 0.5, sm: 0.7 },
                          overflow: 'hidden',
                          textOverflow: { xs: 'ellipsis', sm: 'clip' },
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                          maxWidth: { xs: '5.9375rem', sm: '18.75rem' },
                          lineHeight: { sm: '1.1' },
                          maxHeight: { sm: '1rem' },
                          display: 'block'
                        }
                      }}
                    />
                  </Tooltip>
                ))}
                
                {/* "+X more" chip on mobile only */}
                {!isDesktop && uniqueCertifications.length > 3 && (
                  <Tooltip 
                    title={
                      <React.Fragment>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                          Additional certifications:
                        </Typography>
                        {uniqueCertifications.slice(3).map((cert, index) => (
                          <Typography key={index} variant="caption" display="block" sx={{ pl: 1 }}>
                            • {cert}
                          </Typography>
                        ))}
                      </React.Fragment>
                    }
                    placement="top"
                    arrow
                  >
                    <Chip
                      label={`+${uniqueCertifications.length - 3}`}
                      size="small"
                      sx={{
                        height: '0.9375rem',
                        fontSize: '0.53rem',
                        backgroundColor: 'rgba(240, 248, 240, 0.95)',
                        color: 'rgba(70, 90, 70, 0.95)',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            )}
            
            {/* View Button */}
            <Button
              variant="contained"
              size="small"
              sx={{
                position: 'absolute',
                bottom: { xs: '0.375rem', sm: '0.5rem' },
                right: { xs: '0.125rem', sm: '0.5rem' },
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                height: { xs: '1.375rem', sm: '1.625rem' },
                minWidth: { xs: '2.625rem', sm: '3.125rem' },
                padding: { xs: '0.0625rem 0.3125rem', sm: '0.125rem 0.5rem' },
                borderRadius: '0.25rem',
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                boxShadow: { xs: '0 0.0625rem 0.125rem rgba(0,0,0,0.1)', sm: '0 0.125rem 0.25rem rgba(0,0,0,0.15)' },
                zIndex: 10,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                  boxShadow: { xs: '0 0.125rem 0.25rem rgba(0,0,0,0.15)', sm: '0 0.1875rem 0.375rem rgba(0,0,0,0.2)' },
                }
              }}
              href={product?.affiliate_link || product?.affiliateLink || '#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                logAnalyticsEvent('click_buy_product', {
                  item_id: product?.id || 'unknown',
                  item_name: displayTitle,
                  item_brand: product?.brand || 'unknown',
                  item_category: 'product',
                  supplement_id: product?.supplementKey || 'unknown',
                  item_list_context: 'stack_view'
                });
              }}
            >
              Buy
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};

export default StackProductCard; 